import React, { useState, useEffect, useRef } from 'react';
import { X, Check, GripVertical, RotateCcw } from 'lucide-react';

// Variable global para el listener (fuera del componente)
let globalMouseMoveHandler = null;
let globalGhostElement = null;

const ColumnReorderPopup = ({
  isOpen,
  onClose,
  allColumns,
  currentColumns,
  onSave,
  defaultColumnsIds
}) => {
  const [internalColumns, setInternalColumns] = useState([]);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [ghostElement, setGhostElement] = useState(null);
  const [visible, setVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // 0. Detectar mobile
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 640);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // 1. Bloquear scroll + animación de entrada/salida
  useEffect(() => {
    if (isOpen) {
      const originalBodyOverflow = document.body.style.overflow;
      const originalHtmlOverflow = document.documentElement.style.overflow;

      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';

      // pequeño delay para que la transición de entrada sea visible
      requestAnimationFrame(() => setVisible(true));
      
      return () => {
        document.body.style.overflow = originalBodyOverflow;
        document.documentElement.style.overflow = originalHtmlOverflow;
        setVisible(false);
      };
    }
  }, [isOpen]);

  // 2. Carga inicial
  useEffect(() => {
    if (isOpen) {
      const visibleIds = new Set(currentColumns.map(c => c.id));
      const activePart = currentColumns.map(c => ({ ...c, checked: true }));
      const inactivePart = allColumns
        .filter(c => !visibleIds.has(c.id))
        .map(c => ({ ...c, checked: false }));
      setInternalColumns([...activePart, ...inactivePart]);
    }
  }, [isOpen, currentColumns, allColumns]);

  // 3. Cleanup al desmontar
  useEffect(() => {
    return () => {
      if (globalMouseMoveHandler) {
        document.removeEventListener('dragover', globalMouseMoveHandler);
        globalMouseMoveHandler = null;
      }
    };
  }, []);

  if (!isOpen) return null;

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 280);
  };

  const handleToggle = (id) => {
    setInternalColumns(prev => prev.map(col =>
      col.id === id ? { ...col, checked: !col.checked } : col
    ));
  };

  // --- DRAG AND DROP ---

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';

    // Forzar cursor grabbing de múltiples formas
    const dragElement = e.currentTarget;
    dragElement.style.cursor = 'grabbing';
    document.body.style.cursor = 'grabbing';
    document.documentElement.style.cursor = 'grabbing';

    // Agregar estilos CSS globales MÁS AGRESIVOS
    if (!document.getElementById('dragging-cursor-style')) {
      const style = document.createElement('style');
      style.id = 'dragging-cursor-style';
      style.textContent = `
        * {
          cursor: grabbing !important;
        }
        body, html {
          cursor: grabbing !important;
        }
      `;
      document.head.appendChild(style);
    }

    // Ocultar imagen de drag del navegador
    const img = new Image();
    img.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
    e.dataTransfer.setDragImage(img, 0, 0);

    const rect = dragElement.getBoundingClientRect();

    // Calcular el offset desde donde hizo click
    const offsetX = e.clientX - rect.left;
    const offsetY = e.clientY - rect.top;

    // Guardar info del fantasma
    const ghostInfo = {
      label: internalColumns[index].label,
      width: rect.width,
      height: rect.height,
      initialX: e.clientX,
      initialY: e.clientY,
      offsetX: offsetX,
      offsetY: offsetY
    };

    setGhostElement(ghostInfo);

    // AGREGAR LISTENER INMEDIATAMENTE usando DRAGOVER
    if (globalMouseMoveHandler) {
      document.removeEventListener('dragover', globalMouseMoveHandler);
    }

    globalMouseMoveHandler = (moveEvent) => {
      if (globalGhostElement) {
        globalGhostElement.style.left = (moveEvent.clientX - offsetX) + 'px';
        globalGhostElement.style.top = (moveEvent.clientY - offsetY) + 'px';
      }
    };

    document.addEventListener('dragover', globalMouseMoveHandler);

    // Esperar a que React renderice el fantasma
    setTimeout(() => {
      globalGhostElement = document.getElementById('drag-ghost-element');
    }, 0);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setGhostElement(null);

    // Restaurar cursor normal en todos los lugares
    document.body.style.cursor = '';
    document.documentElement.style.cursor = '';
    document.body.classList.remove('dragging-column');

    // Remover estilo temporal
    const style = document.getElementById('dragging-cursor-style');
    if (style) {
      style.remove();
    }

    if (globalMouseMoveHandler) {
      document.removeEventListener('dragover', globalMouseMoveHandler);
      globalMouseMoveHandler = null;
    }
    globalGhostElement = null;
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnter = (e, targetIndex) => {
    e.preventDefault();

    if (draggedIndex === null || draggedIndex === targetIndex) return;

    setInternalColumns(prev => {
      const newColumns = [...prev];
      const [draggedColumn] = newColumns.splice(draggedIndex, 1);
      newColumns.splice(targetIndex, 0, draggedColumn);
      return newColumns;
    });

    setDraggedIndex(targetIndex);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    handleDragEnd();
  };

  // --- GUARDAR Y RESET ---

  const handleSave = () => {
    const finalColumns = internalColumns
      .filter(col => col.checked)
      .map(({ checked, ...rest }) => rest);
    onSave(finalColumns);
    handleClose();
  };

  const handleReset = () => {
    const defaults = defaultColumnsIds.map(id => {
      const found = allColumns.find(c => c.id === id);
      return found ? { ...found, checked: true } : null;
    }).filter(Boolean);

    const defaultIdsSet = new Set(defaultColumnsIds);
    const others = allColumns
      .filter(c => !defaultIdsSet.has(c.id))
      .map(c => ({ ...c, checked: false }));
    setInternalColumns([...defaults, ...others]);
  };

  return (
    <>
      {/* Overlay */}
      <div
        onClick={handleClose}
        style={{ transition: 'opacity 0.28s ease' }}
        className={`fixed inset-0 z-[9999] bg-black backdrop-blur-sm
          ${visible ? 'bg-opacity-50' : 'bg-opacity-0 pointer-events-none'}`}
      />

      {/* Panel — bottom-sheet en mobile, modal centrado en desktop */}
      <div
        style={{ transition: 'transform 0.28s cubic-bezier(0.4,0,0.2,1), opacity 0.28s ease' }}
        className={`
          fixed z-[10000] flex flex-col bg-white overflow-hidden shadow-2xl
          /* mobile: floating bottom sheet with margins */
          bottom-4 left-4 right-4 rounded-2xl max-h-[85vh]
          /* desktop: modal centrado con bordes completos redondeados */
          sm:bottom-auto sm:left-1/2 sm:top-1/2 sm:right-auto
          sm:-translate-x-1/2 sm:-translate-y-1/2
          sm:w-full sm:max-w-lg sm:rounded-xl sm:max-h-[85vh]
          ${visible
            ? 'translate-y-0 opacity-100'
            : 'translate-y-[120%] sm:translate-y-[-48%] opacity-0 pointer-events-none'
          }
        `}
      >
        {/* Header */}
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-b flex justify-between items-center bg-white shrink-0">
          <h3 className="text-base sm:text-lg font-bold text-gray-800">Personalizar Columnas</h3>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-100"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-3 sm:p-4 bg-gray-50">
          <div className="space-y-2">
            {internalColumns.map((col, index) => {
              const isDragging = draggedIndex === index;

              return (
                <div
                  key={col.id}
                  draggable={col.checked}

                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragEnd={handleDragEnd}
                  onDragOver={handleDragOver}
                  onDragEnter={(e) => handleDragEnter(e, index)}
                  onDrop={handleDrop}

                  style={{
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}

                  className={`
                    relative flex items-center p-3 rounded-lg select-none
                    ${col.checked
                      ? 'cursor-grab active:cursor-grabbing'
                      : 'opacity-60 bg-gray-50 border border-gray-100 cursor-not-allowed'
                    }
                    ${isDragging
                      ? 'bg-blue-50 border-2 border-dashed border-blue-300 opacity-40'
                      : 'bg-white border border-gray-200 shadow-sm hover:border-blue-300 hover:shadow-md'
                    }
                  `}
                >
                  <div className={`flex items-center w-full transition-opacity duration-150 ${isDragging ? 'opacity-0' : 'opacity-100'}`}>
                    <div className="mr-3 flex items-center">
                      <input
                        type="checkbox"
                        checked={col.checked}
                        onChange={() => handleToggle(col.id)}
                        className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500 border-gray-300 cursor-pointer"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                    <div className="flex-1 font-medium text-gray-700 text-sm sm:text-base">
                      {col.label}
                    </div>
                    {col.checked && (
                      <div className="text-gray-400 pl-2 cursor-grab active:cursor-grabbing">
                        <GripVertical size={20} />
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-t bg-white shrink-0">
          {/* Mobile: stack vertical / Desktop: fila horizontal */}
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-between sm:items-center sm:gap-0">
            <button
              onClick={handleReset}
              className="flex items-center justify-center sm:justify-start text-sm text-gray-500 hover:text-blue-600 transition-colors px-2 py-2 sm:py-1 rounded hover:bg-blue-50"
            >
              <RotateCcw size={14} className="mr-2" />
              Restaurar default
            </button>

            <div className="flex items-center gap-2 sm:gap-3">
              <button
                onClick={handleClose}
                className="flex-1 sm:flex-none px-4 py-2.5 sm:py-2 text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                className="flex-1 sm:flex-none px-4 py-2.5 sm:py-2 text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white rounded-lg shadow-md transition-colors flex items-center justify-center"
              >
                <Check size={16} className="mr-2" />
                Aplicar cambios
              </button>
            </div>
          </div>
        </div>

      </div>

      {/* FANTASMA CON ID PARA ACCESO DIRECTO */}
      {ghostElement && (
        <div
          id="drag-ghost-element"
          style={{
            position: 'fixed',
            left: (ghostElement.initialX - ghostElement.offsetX) + 'px',
            top: (ghostElement.initialY - ghostElement.offsetY) + 'px',
            width: ghostElement.width + 'px',
            height: ghostElement.height + 'px',
            pointerEvents: 'none',
            zIndex: 99999,
            opacity: 1,
            cursor: 'grabbing', // Cursor de agarre
          }}
          className="bg-white border-2 border-blue-500 rounded-lg shadow-2xl flex items-center p-3"
        >
          <div className="mr-3 w-5 h-5 bg-blue-600 rounded flex items-center justify-center shrink-0">
            <Check size={14} className="text-white" />
          </div>

          <div className="flex-1 font-semibold text-gray-800">
            {ghostElement.label}
          </div>

          <div className="text-gray-400 pl-2 shrink-0">
            <GripVertical size={20} />
          </div>
        </div>
      )}
    </>
  );
};

export default ColumnReorderPopup;